// הגדלה — מחווה אחת לכל בלוק וליצירה שלמה (web-ai-stage-layout). overlay של האפליקציה, לא Fullscreen API של הדפדפן
// (נכשל ב-iOS Safari ומאבד את שדה הכתיבה). אותו אלמנט עובר למצב fixed ([data-expanded]) — בלי portal ובלי ציור מחדש:
// המיון/סינון של הטבלה נשמרים, ECharts מתאים את עצמו ב-ResizeObserver הקיים, ה-iframe של app לא נטען מחדש, ואין עותק
// שני ב-DOM. Esc / "חזור" / לחיצה על השוליים סוגרים; פוקוס עובר ל"חזור" ובסגירה חוזר לכפתור "הגדל". מקום הבלוק בדף נשמר
// (גובה קבוע למקום הריק) והדף לא נגלל מתחת — סגירה מחזירה לאותה גלילה. z-index מתחת ל-drawer-ים (?consent= נפתח מעליו).
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { S } from './strings.ts';
import { plainDash } from './format.ts';
import { LayerContext } from './context.tsx';

/**
 * מי שמחזיק את המסך (השיחה) יכול לשלוט בהגדלה: open/onOpenChange (אייבי ביקשה `focus:'expand'`, "בלוק חדש · הצג"),
 * footer — שדה הכתיבה בתחתית ההגדלה, chips — צ'יפים בשורה העליונה ("תשובה חדשה · הצג"). בלי host — מצב מקומי.
 */
export type ExpandHost = { open?: boolean; onOpenChange?: (open: boolean) => void; footer?: ReactNode; chips?: ReactNode };
export const ExpandHostContext = createContext<ExpandHost | null>(null);

type Ctl = { expanded: boolean; open: () => void; btn: React.MutableRefObject<HTMLButtonElement | null> };
const ExpandCtl = createContext<Ctl | null>(null);

/** האם יש דיאלוג מודאלי אחר פתוח מעל (drawer של קונסנט/תפריט) — Esc שלו, לא של ההגדלה. */
function otherModalOpen(self: HTMLElement | null) {
  for (const d of document.querySelectorAll('[role="dialog"][aria-modal="true"]')) if (d !== self && !self?.contains(d) && !d.contains(self)) return true;
  return false;
}

let locks = 0;
function lockScroll(on: boolean) {
  locks += on ? 1 : -1;
  document.documentElement.toggleAttribute('data-ab-expanded', locks > 0);
}

export function Expand({ children, labelHe, testId, className }: { children: ReactNode; labelHe?: string; testId?: string; className?: string }) {
  const host = useContext(ExpandHostContext);
  const layers = useContext(LayerContext);
  const controlled = host?.open !== undefined;
  const [local, setLocal] = useState(false);
  const expanded = controlled ? !!host!.open : local;
  const hostRef = useRef(host);
  hostRef.current = host;
  const set = useCallback(
    (o: boolean) => {
      if (hostRef.current?.open === undefined) setLocal(o);
      hostRef.current?.onOpenChange?.(o);
    },
    [],
  );
  const slot = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const back = useRef<HTMLButtonElement>(null);
  const btn = useRef<HTMLButtonElement | null>(null);
  // המקום של הבלוק בדף נשאר בגובהו כשהוא מוגדל (אחרת הדף מתקצר והגלילה קופצת) — הגובה האחרון שנמדד לפני ההגדלה
  const lastH = useRef(0);
  const expRef = useRef(expanded);
  expRef.current = expanded;
  useEffect(() => {
    const el = slot.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (!expRef.current) lastH.current = el.getBoundingClientRect().height;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const hold = expanded ? lastH.current : 0;

  useEffect(() => {
    if (!expanded) return;
    lockScroll(true);
    const from = document.activeElement as HTMLElement | null;
    back.current?.focus({ preventScroll: true });
    // Esc: במחסנית השכבות של האפליקציה (web-screen-budget) — סוגר רק כשההגדלה היא העליונה; בלי מחסנית — מאזין משלו
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || otherModalOpen(box.current)) return;
      e.preventDefault();
      set(false);
    };
    const off = layers ? layers.push(() => set(false)) : null;
    if (!off) window.addEventListener('keydown', onKey);
    return () => {
      if (off) off();
      else window.removeEventListener('keydown', onKey);
      lockScroll(false);
      (btn.current ?? from)?.focus?.({ preventScroll: true });
    };
  }, [expanded, set, layers]);

  const ctl: Ctl = { expanded, open: () => set(true), btn };
  return (
    <div ref={slot} className={`ab-expand-slot${className ? ` ${className}` : ''}`} style={hold ? { minBlockSize: `${hold}px` } : undefined}>
      <div
        ref={box}
        className="ab-expand"
        data-expanded={expanded || undefined}
        data-testid={testId}
        role={expanded ? 'dialog' : undefined}
        aria-modal={expanded || undefined}
        aria-label={expanded ? (labelHe ? plainDash(labelHe) : S.expanded) : undefined}
        onClick={expanded ? (e) => e.target === e.currentTarget && set(false) : undefined}
      >
        {expanded ? (
          <div className="ab-expand-bar">
            <button ref={back} type="button" className="ab-btn ab-expand-back" onClick={() => set(false)} data-testid="expand-close">
              {S.expandBack}
            </button>
            {host?.chips}
          </div>
        ) : null}
        <div className="ab-expand-body">
          <ExpandCtl.Provider value={ctl}>{children}</ExpandCtl.Provider>
        </div>
        {expanded && host?.footer ? <div className="ab-expand-foot">{host.footer}</div> : null}
      </div>
    </div>
  );
}

/** כפתור "הגדל" — בראש הבלוק; מחוץ ל-Expand (PDF, בלוק text/raw) — לא מצויר. במצב מוגדל — "חזור" בשורה העליונה במקומו. */
export function ExpandButton({ labelHe, testId = 'expand-btn' }: { labelHe?: string; testId?: string }) {
  const c = useContext(ExpandCtl);
  if (!c || c.expanded) return null;
  return (
    <button
      ref={(el) => {
        c.btn.current = el;
      }}
      type="button"
      className="ab-btn ab-btn-ghost ab-btn-expand"
      onClick={c.open}
      aria-label={labelHe ? `${S.expand}: ${plainDash(labelHe)}` : S.expand}
      data-testid={testId}
    >
      {S.expand}
    </button>
  );
}

/** האם הבלוק בתוך Expand (כותרת הבלוק מצוירת גם בלי titleHe — בשביל "הגדל"). */
export const useExpandable = () => useContext(ExpandCtl) !== null;

/** מצב ההגדלה של הבלוק ופתיחתה (טבלה compact: "עוד N שורות" פותח הגדלה). מחוץ ל-Expand — null. */
export function useExpand(): { expanded: boolean; open: () => void } | null {
  const c = useContext(ExpandCtl);
  return c ? { expanded: c.expanded, open: c.open } : null;
}

/** ראש בלוק: כותרת, הכפתורים של הבלוק, "הגדל" בסוף. בלי כותרת, בלי כפתורים ומחוץ ל-Expand — לא מצויר. */
export function BlockHead({ titleHe, children }: { titleHe?: string | null; children?: ReactNode }) {
  const c = useContext(ExpandCtl);
  // בלי כותרת ובלי כפתורים: הראש קיים רק בשביל "הגדל" — במצב מוגדל ("חזור" למעלה) או מחוץ ל-Expand אין מה להציג
  if (!titleHe && !children && (!c || c.expanded)) return null;
  return (
    <header className="ab-block-head">
      {titleHe ? <h3>{plainDash(titleHe)}</h3> : <span className="ab-head-fill" />}
      {children}
      <ExpandButton labelHe={titleHe ?? undefined} />
    </header>
  );
}
